import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Users, TrendingUp } from "lucide-react";
import { MaskedLine, Reveal } from "../site/Reveal";
import { PrimaryButton, OutlineButton } from "../site/Buttons";
import { trackEvent } from "../../lib/analytics";
import { HERO_IMG } from "../../lib/site";

const FEATURES = [
  { Icon: ShieldCheck, title: "Private & Secure", text: "Invite-only community with verified members." },
  { Icon: Users, title: "Collaborate & Grow", text: "Connect, collaborate, and create opportunities." },
  { Icon: TrendingUp, title: "Build Wealth", text: "Multiple streams. Lasting legacy." },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.25]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-[720px] lg:min-h-[800px] flex items-center overflow-hidden bg-[#020A12]">
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <img src={HERO_IMG} alt="MST — city skyline" className="w-full h-[115%] object-cover object-[70%_center]" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-[#020A12] via-[#020A12]/70 to-[#020A12]/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#020A12] via-[#020A12]/25 to-[#020A12]/55" />

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 w-full pt-28 pb-16">
        <div className="max-w-2xl">
          <MaskedLine delay={0.1}>
            <span
              className="inline-flex items-center gap-3 text-xs font-extrabold uppercase tracking-[0.35em] text-[#00C4F4]"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              <span className="h-px w-10 bg-gradient-to-r from-[#006FD6] to-[#00C4F4]" />
              Multiple Stream Team
            </span>
          </MaskedLine>

          <h1
            data-testid="hero-headline"
            className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.08]"
          >
            <MaskedLine delay={0.25}>Multiple Streams.</MaskedLine>
            <MaskedLine delay={0.4}>One Team.</MaskedLine>
            <MaskedLine delay={0.55} className="text-[#009BFF]">Infinite Impact.</MaskedLine>
          </h1>

          <Reveal delay={0.75} y={16}>
            <p className="mt-8 text-base sm:text-lg text-[#B7C0CC] leading-[1.8] max-w-xl">
              MST is a private, invite-only network of entrepreneurs, investors, founders,
              business owners, and leaders building multiple streams of income and
              generational wealth together.
            </p>
          </Reveal>

          <Reveal delay={0.9} y={16} className="mt-10 flex flex-wrap gap-4">
            <PrimaryButton to="/apply" data-testid="hero-apply-btn" onClick={() => trackEvent("apply_membership_click", { location: "hero" })}>Apply for Membership</PrimaryButton>
            <OutlineButton to="/about" data-testid="hero-learn-more-btn">Learn More</OutlineButton>
          </Reveal>

          <Reveal delay={1.05} y={16} className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-9 sm:gap-8">
            {FEATURES.map(({ Icon, title, text }) => (
              <div key={title} className="flex items-start gap-3.5" data-testid={`hero-feature-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="w-11 h-11 rounded-full border border-[#009BFF]/40 bg-[#009BFF]/10 flex items-center justify-center shrink-0">
                  <Icon size={20} strokeWidth={1.5} className="text-[#00C4F4]" />
                </div>
                <div>
                  <h3 className="text-xs font-extrabold uppercase tracking-widest text-white" style={{ fontFamily: "var(--font-heading)" }}>
                    {title}
                  </h3>
                  <p className="mt-1.5 text-sm text-[#B7C0CC] leading-relaxed">{text}</p>
                </div>
              </div>
            ))}
          </Reveal>
        </div>
      </motion.div>
    </section>
  );
};
